import React, { useEffect, useState } from 'react';
import { Button, ButtonGroup, Container, Form, Table } from 'react-bootstrap';
import './settings.css';
import { get, getDatabase, ref, set } from 'firebase/database';
import { firebaseApp } from '../../../index';
import DatePicker, { DateObject } from 'react-multi-date-picker';
import { DEFAULT_COMMISSION_RULE } from '../../../Util';
import { OverrideItem, Rule, RulePage } from '../../../@types';
import { Importer, ImporterField } from 'react-csv-importer';
import 'react-csv-importer/dist/index.css';
import CSVImporter from './CSVImporter';
import CSVImporterOverrideCommission from './CSVImporterOverrideCommission';

const toRulePage = (rule: Rule): RulePage => {
  return {
    ...rule,
    override: rule.override
      ? Object.keys(rule.override).map((day) => {
          const override = rule.override![day];
          let items: OverrideItem[] = [];
          if (override) {
            Object.keys(override).forEach((sku) => {
              const value = override[sku];
              if (typeof value === 'number') {
                items.push({
                  sku,
                  commission: value,
                });
              }
            });
          }
          return {
            day,
            items,
          };
        })
      : [],
  };
};

const toRule = (rule: RulePage): Rule => {
  return {
    ...rule,
    override: rule.override.reduce((acc, curr) => {
      acc[curr.day] = curr.items.reduce((acc, curr) => {
        acc[curr.sku] = curr.commission;
        return acc;
      }, {} as any);
      return acc;
    }, {} as any),
  };
};

export const CommissionSettings = () => {
  const [reportMonth, setReportMonth] = useState(new Date());
  const [busy, setBusy] = useState(false);
  const [rule, setRule] = useState<RulePage>(DEFAULT_COMMISSION_RULE);

  useEffect(() => {
    const database = getDatabase(firebaseApp);
    const year = reportMonth.getFullYear();
    const month = (reportMonth.getMonth() + 1).toString().padStart(2, '0');
    const ruleRef = ref(database, `/commission_rule/${year + month}`);
    get(ruleRef).then((rule) => {
      if (!rule.val()) {
        return DEFAULT_COMMISSION_RULE;
      }
      setRule(toRulePage(rule.val()));
    });
  }, [reportMonth]);

  const onSubmit = async () => {
    setBusy(true);
    try {
      const database = getDatabase(firebaseApp);
      const year = reportMonth.getFullYear();
      const month = (reportMonth.getMonth() + 1).toString().padStart(2, '0');
      const ruleRef = ref(database, `/commission_rule/${year + month}`);
      await set(ruleRef, toRule(rule));
      window.alert('Commission settings saved!');
      setBusy(false);
    } catch (e) {
      window.alert(`Error: ${(e as any).toString()}`);
    }
  };

  const onAddNewOverride = () => {
    setRule((rule) => {
      const newRule = { ...rule };
      const days = new Array(31).fill(0).map((_, i) => `${i + 1}`.padStart(2, '0'));
      const usedDays = newRule.override.map((o) => o.day);
      const nextAvailableDays = days.filter((e) => !newRule.override.find((o) => o.day === e));
      console.log('usedDays', usedDays);
      console.log('nextAvailableDays', nextAvailableDays);
      newRule.override.push({
        day: nextAvailableDays[0],
        items: [],
      });
      return newRule;
    });
  };

  const onChangeOverrideDay = (index: number, day: string) => {
    setRule((rule) => {
      const newRule = { ...rule };
      newRule.override[index].day = day;
      return newRule;
    });
  };

  const onRemoveOverride = (index: number) => {
    setRule((rule) => {
      const newRule = { ...rule };
      newRule.override.splice(index, 1);
      return newRule;
    });
  };

  const onAddOverrideItem = (index: number) => {
    setRule((rule) => {
      const newRule = { ...rule };
      newRule.override[index].items.push({
        sku: '',
        commission: 0,
      });
      return newRule;
    });
  };

  const onRemoveOverrideItem = (index: number, itemIndex: number) => {
    setRule((rule) => {
      const newRule = { ...rule };
      newRule.override[index].items.splice(itemIndex, 1);
      return newRule;
    });
  };

  const onChangeOverrideSKU = (index: number, itemIndex: number, sku: string) => {
    setRule((rule) => {
      const newRule = { ...rule };
      newRule.override[index].items[itemIndex].sku = sku;
      return newRule;
    });
  };

  const onChangeOverrideCommission = (index: number, itemIndex: number, value: string) => {
    setRule((rule) => {
      const newRule = { ...rule };
      newRule.override[index].items[itemIndex].commission = parseFloat(value);
      return newRule;
    });
  };

  const onChangeValue = (path: (string | number)[], value: any) => {
    setRule((rule) => {
      const newRule = { ...rule };
      path.reduce((acc, curr, i, path) => {
        if (i === path.length - 1) {
          if (typeof value === 'string') {
            return (acc[curr] = parseFloat(value));
          }
          return (acc[curr] = value);
        }
        if (acc[curr].length) {
          return (acc[curr] = [...acc[curr]]);
        }
        return (acc[curr] = {
          ...acc[curr],
        });
      }, newRule as any);
      return newRule;
    });
  };

  const onRemove = (path: (string | number)[]) => {
    setRule((rule) => {
      const newRule = { ...rule };
      path.reduce((acc, curr, i, path) => {
        if (i === path.length - 1) {
          // if type is number, it's mutating an array
          if (typeof curr === 'number') {
            acc.splice(curr, 1);
            return acc;
          }
          return delete acc[curr];
        }
        if (acc[curr].length) {
          return (acc[curr] = [...acc[curr]]);
        }
        return (acc[curr] = {
          ...acc[curr],
        });
      }, newRule as any);
      console.log(rule, newRule);
      return newRule;
    });
  };
  const handleDownloadTemplate = () => {
    // http://127.0.0.1:5001/sk-srikandi/asia-southeast1/query-overrideCommissionTemplate?secret=QAoL5xN6Up
    window.open(
      // `http://127.0.0.1:5001/sk-srikandi/asia-southeast1/query-overrideCommissionTemplate?secret=QAoL5xN6Up`,
      `https://asia-southeast1-sk-srikandi.cloudfunctions.net/query-overrideCommissionTemplate?secret=QAoL5xN6Up`,
      '_blank',
    );
  };

  rule.override.map((e) => {
    console.log('e', e);
  });
  return (
    <Container>
      <h2 className="header">Import Commissions</h2>
      <div>
        <CSVImporter date={reportMonth} />
      </div>
      <h2 className="header">Commission Settings</h2>
      <div style={{ textAlign: 'center' }}>
        <DatePicker
          onlyMonthPicker
          format="MMMM YYYY"
          onChange={(data) => {
            // @ts-ignore
            if (data) {
              const date = (data as DateObject).toDate();
              date.setHours(24);
              setReportMonth(date);
            }
          }}
          value={reportMonth}
          minDate="2022/12/01"
          style={{
            border: '1px solid var(--primaryColor)',
            height: '20px',
            width: '120px',
            textAlign: 'center',
            fontSize: '0.6rem',
          }}
        />
      </div>
      <Form className="formMargin">
        <div>
          <span className="header-1">Bonus Cash</span>
          <div
            style={{
              display: 'flex',
            }}
          >
            <div
              className="group-section"
              style={{
                width: '50%',
                marginRight: '5px',
              }}
            >
              <div
                style={{
                  padding: '4%',
                }}
              >
                <h5 className="subheader">Individual</h5>
                <Form.Group className="mb-3 smallTitle">
                  <Form.Label>Min Sale</Form.Label>
                  <Form.Control
                    onChange={(event) =>
                      onChangeValue(['bonus_cash', 'individual', 'min_sale'], event.target.value)
                    }
                    value={rule.bonus_cash.individual.min_sale}
                    type="number"
                  />
                </Form.Group>
                <span className="title">Tier</span>
                {rule.bonus_cash.individual.tier.map((individualTier, i) => {
                  return (
                    <div className="tier-group" key={i}>
                      <div
                        style={{
                          display: 'flex',
                        }}
                      >
                        <div
                          style={{
                            width: '50%',
                            marginRight: '5px',
                          }}
                        >
                          <Form.Group className="mb-3 smallTitle">
                            <Form.Label>Cash Bonus</Form.Label>
                            <Form.Control
                              onChange={(event) =>
                                onChangeValue(
                                  ['bonus_cash', 'individual', 'tier', i, 'cash_bonus'],
                                  event.target.value,
                                )
                              }
                              value={individualTier.cash_bonus}
                              type="number"
                            />
                          </Form.Group>
                        </div>
                        <div
                          style={{
                            width: '50%',
                            marginLeft: '5px',
                          }}
                        >
                          <Form.Group className="mb-3 smallTitle">
                            <Form.Label>Min Sale</Form.Label>
                            <Form.Control
                              onChange={(event) =>
                                onChangeValue(
                                  ['bonus_cash', 'individual', 'tier', i, 'min_sale'],
                                  event.target.value,
                                )
                              }
                              value={individualTier.min_sale}
                              type="number"
                            />
                          </Form.Group>
                        </div>
                      </div>
                      <div className="remove-tier">
                        <Button
                          onClick={() => onRemove(['bonus_cash', 'individual', 'tier', i])}
                          style={{
                            color: 'var(--accentColor)',
                            backgroundColor: '#e4572e00',
                            border: 'none',
                            paddingTop: '0',
                            fontSize: '14px',
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  );
                })}
                <div>
                  <Button
                    onClick={() =>
                      onChangeValue(
                        [
                          'bonus_cash',
                          'individual',
                          'tier',
                          rule.bonus_cash.individual.tier.length,
                        ],
                        { min_sale: 0, cash_bonus: 0 },
                      )
                    }
                    style={{
                      color: 'var(--secondaryColor)',
                      backgroundColor: 'var(--accentColor)',
                      border: '1px solid var(--accentColor)',
                    }}
                  >
                    Add Tier
                  </Button>
                </div>
              </div>
            </div>
            <br />
            <div
              className="group-section"
              style={{
                width: '50%',
                marginLeft: '5px',
              }}
            >
              <div
                style={{
                  padding: '4%',
                }}
              >
                <h5 className="subheader">Group</h5>
                <Form.Group className="mb-3 smallTitle">
                  <Form.Label>Min Sale</Form.Label>
                  <Form.Control
                    onChange={(event) =>
                      onChangeValue(['bonus_cash', 'group', 'min_sale'], event.target.value)
                    }
                    value={rule.bonus_cash.group.min_sale}
                    type="number"
                  />
                </Form.Group>
                <span className="title">Tier</span>
                {rule.bonus_cash.group.tier.map((groupTier, i) => {
                  return (
                    <div key={i} className="tier-group">
                      <div
                        style={{
                          display: 'flex',
                        }}
                      >
                        <div
                          style={{
                            width: '50%',
                            marginRight: '5px',
                          }}
                        >
                          <Form.Group className="mb-3 smallTitle">
                            <Form.Label>Cash Bonus</Form.Label>
                            <Form.Control
                              onChange={(event) =>
                                onChangeValue(
                                  ['bonus_cash', 'group', 'tier', i, 'cash_bonus'],
                                  event.target.value,
                                )
                              }
                              value={groupTier.cash_bonus}
                              type="number"
                            />
                          </Form.Group>
                        </div>
                        <div
                          style={{
                            width: '50%',
                            marginLeft: '5px',
                          }}
                        >
                          <Form.Group className="mb-3 smallTitle">
                            <Form.Label>Min Sale</Form.Label>
                            <Form.Control
                              onChange={(event) =>
                                onChangeValue(
                                  ['bonus_cash', 'group', 'tier', i, 'min_sale'],
                                  event.target.value,
                                )
                              }
                              value={groupTier.min_sale}
                              type="number"
                            />
                          </Form.Group>
                        </div>
                      </div>
                      <div className="remove-tier">
                        <Button
                          onClick={() => onRemove(['bonus_cash', 'group', 'tier', i])}
                          style={{
                            color: 'var(--accentColor)',
                            backgroundColor: '#e4572e00',
                            border: 'none',
                            paddingTop: '0',
                            fontSize: '14px',
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  );
                })}
                <div>
                  <Button
                    onClick={() =>
                      onChangeValue(
                        ['bonus_cash', 'group', 'tier', rule.bonus_cash.group.tier.length],
                        { min_sale: 0, cash_bonus: 0 },
                      )
                    }
                    style={{
                      color: 'var(--secondaryColor)',
                      backgroundColor: 'var(--accentColor)',
                      border: '1px solid var(--accentColor)',
                    }}
                  >
                    Add Tier
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <br></br>

        <div>
          <span className="header-1">Sales Commission</span>
          <div
            style={{
              display: 'flex',
            }}
          >
            <div
              className="group-section"
              style={{
                width: '50%',
                marginRight: '5px',
              }}
            >
              <div
                style={{
                  padding: '4%',
                }}
              >
                <h5 className="subheader">Srikandi</h5>
                {rule.sales_commission.srikandi.map((srikandi, i) => {
                  return (
                    <div key={i} className="tier-group">
                      <div
                        style={{
                          display: 'flex',
                        }}
                      >
                        <div
                          style={{
                            width: '50%',
                            marginRight: '5px',
                          }}
                        >
                          <Form.Group className="mb-3 smallTitle">
                            <Form.Label>Commission %</Form.Label>
                            <Form.Control
                              onChange={(event) =>
                                onChangeValue(
                                  ['sales_commission', 'srikandi', i, 'commission'],
                                  (parseFloat(event.target.value) / 100).toFixed(2),
                                )
                              }
                              value={(srikandi.commission * 100).toFixed(2)}
                              type="number"
                            />
                          </Form.Group>
                        </div>
                        <div
                          style={{
                            width: '50%',
                            marginLeft: '5px',
                          }}
                        >
                          <Form.Group className="mb-3 smallTitle">
                            <Form.Label>Min Sale</Form.Label>
                            <Form.Control
                              onChange={(event) =>
                                onChangeValue(
                                  ['sales_commission', 'srikandi', i, 'min_sale'],
                                  event.target.value,
                                )
                              }
                              value={srikandi.min_sale}
                              type="number"
                            />
                          </Form.Group>
                        </div>
                      </div>
                      <div className="remove-tier">
                        <Button
                          onClick={() => onRemove(['sales_commission', 'srikandi', i])}
                          style={{
                            color: 'var(--accentColor)',
                            backgroundColor: '#e4572e00',
                            border: 'none',
                            paddingTop: '0',
                            fontSize: '14px',
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  );
                })}
                <div>
                  <Button
                    onClick={() =>
                      onChangeValue(
                        ['sales_commission', 'srikandi', rule.sales_commission.srikandi.length],
                        { min_sale: 0, commission: 0 },
                      )
                    }
                    style={{
                      color: 'var(--secondaryColor)',
                      backgroundColor: 'var(--accentColor)',
                      border: '1px solid var(--accentColor)',
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>
            </div>
            <br />
            <div
              className="group-section"
              style={{
                width: '50%',
                marginLeft: '5px',
              }}
            >
              <div
                style={{
                  padding: '4%',
                }}
              >
                <h5 className="subheader">Srikandi Emas</h5>
                {rule.sales_commission.srikandi_emas.map((se, i) => {
                  return (
                    <div className="tier-group">
                      <div
                        style={{
                          display: 'flex',
                        }}
                      >
                        <div
                          style={{
                            width: '50%',
                            marginRight: '5px',
                          }}
                        >
                          <Form.Group className="mb-3 smallTitle">
                            <Form.Label>Commission</Form.Label>
                            <Form.Control
                              onChange={(event) =>
                                onChangeValue(
                                  ['sales_commission', 'srikandi_emas', i, 'commission'],
                                  (parseFloat(event.target.value) / 100).toFixed(2),
                                )
                              }
                              value={(se.commission * 100).toFixed(2)}
                              type="number"
                            />
                          </Form.Group>
                        </div>
                        <div
                          style={{
                            width: '50%',
                            marginLeft: '5px',
                          }}
                        >
                          <Form.Group className="mb-3 smallTitle">
                            <Form.Label>Min Sale</Form.Label>
                            <Form.Control
                              onChange={(event) =>
                                onChangeValue(
                                  ['sales_commission', 'srikandi_emas', i, 'min_sale'],
                                  event.target.value,
                                )
                              }
                              value={se.min_sale}
                              type="number"
                            />
                          </Form.Group>
                        </div>
                      </div>
                      <div className="remove-tier">
                        <Button
                          onClick={() => onRemove(['sales_commission', 'srikandi_emas', i])}
                          style={{
                            color: 'var(--accentColor)',
                            backgroundColor: '#e4572e00',
                            border: 'none',
                            paddingTop: '0',
                            fontSize: '14px',
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  );
                })}
                <div>
                  <Button
                    onClick={() =>
                      onChangeValue(
                        [
                          'sales_commission',
                          'srikandi_emas',
                          rule.sales_commission.srikandi_emas.length,
                        ],
                        { min_sale: 0, commission: 0 },
                      )
                    }
                    style={{
                      color: 'var(--secondaryColor)',
                      backgroundColor: 'var(--accentColor)',
                      border: '1px solid var(--accentColor)',
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <br />
        <span className="header-1">Target Sales</span>
        <div
          style={{
            display: 'flex',
          }}
        >
          <div
            className="group-section"
            style={{
              width: '100%',
            }}
          >
            <div
              style={{
                padding: '4%',
              }}
            >
              <div className="tier-group">
                <div
                  style={{
                    display: 'flex',
                  }}
                >
                  <div
                    style={{
                      width: '25%',
                      marginLeft: '5px',
                    }}
                  >
                    <Form.Group className="mb-3 smallTitle">
                      <Form.Label>Commission %</Form.Label>
                      <Form.Control
                        onChange={(event) =>
                          onChangeValue(
                            ['target_sale', 'commission'],
                            (parseFloat(event.target.value) / 100).toFixed(2),
                          )
                        }
                        value={(rule.target_sale.commission * 100).toFixed(2)}
                        type="number"
                      />
                    </Form.Group>
                  </div>
                  <div
                    style={{
                      width: '25%',
                      marginLeft: '5px',
                    }}
                  >
                    <Form.Group className="mb-3 smallTitle">
                      <Form.Label>Min Children</Form.Label>
                      <Form.Control
                        onChange={(event) =>
                          onChangeValue(['target_sale', 'min_children'], event.target.value)
                        }
                        value={rule.target_sale.min_children}
                        type="number"
                      />
                    </Form.Group>
                  </div>
                  <div
                    style={{
                      width: '25%',
                      marginLeft: '5px',
                    }}
                  >
                    <Form.Group className="mb-3 smallTitle">
                      <Form.Label>Srikandi</Form.Label>
                      <Form.Control
                        onChange={(event) =>
                          onChangeValue(['target_sale', 'srikandi'], event.target.value)
                        }
                        value={rule.target_sale.srikandi}
                        type="number"
                      />
                    </Form.Group>
                  </div>
                  <div
                    style={{
                      width: '25%',
                      marginLeft: '5px',
                    }}
                  >
                    <Form.Group className="mb-3 smallTitle">
                      <Form.Label>Srikandi Emas</Form.Label>
                      <Form.Control
                        onChange={(event) =>
                          onChangeValue(['target_sale', 'srikandi_emas'], event.target.value)
                        }
                        value={rule.target_sale.srikandi_emas}
                        type="number"
                      />
                    </Form.Group>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <br />

        {/* Override */}
        <div>
          <span className="header-1">Override Commission</span>
          <div>
            <Button variant="outline-dark" onClick={handleDownloadTemplate}>
              Download Override Template
            </Button>
            <br></br>
            <br></br>
            <CSVImporterOverrideCommission date={reportMonth} />
            <br></br>
            {/* {rule.override.map((or, i) => {
              return (
                <div>
                  <Form.Group className="mb-3 smallTitle">
                    <Form.Label>Day</Form.Label>
                    <Form.Select onChange={(event) => onChangeOverrideDay(i, event.target.value)}>
                      {new Array(31)
                        .fill(0)
                        .map((_, i) => `${i + 1}`.padStart(2, '0'))
                        .filter(
                          (value) =>
                            value === or.day || !rule.override.find((or) => or.day === value),
                        )
                        .map((value) => (
                          <option selected={value === or.day} key={value} value={value}>
                            {value}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>
                  <div>
                    {or.items.map((item, j) => {
                      return (
                        <div>
                          <div
                            style={{
                              width: '50%',
                              marginLeft: '5px',
                            }}
                          >
                            <Form.Group className="mb-3 smallTitle">
                              <Form.Label>SKU</Form.Label>
                              <Form.Control
                                onChange={(event) => onChangeOverrideSKU(i, j, event.target.value)}
                                value={item.sku}
                                type="text"
                              />
                            </Form.Group>
                          </div>
                          <div
                            style={{
                              width: '50%',
                              marginLeft: '5px',
                            }}
                          >
                            <Form.Group className="mb-3 smallTitle">
                              <Form.Label>Override Commission (RM)</Form.Label>
                              <Form.Control
                                onChange={(event) =>
                                  onChangeOverrideCommission(i, j, event.target.value)
                                }
                                value={item.commission}
                                type="number"
                              />
                            </Form.Group>
                          </div>
                          <Button
                            style={{
                              marginRight: '5px',
                              color: 'var(--primaryColor)',
                              backgroundColor: 'var(--secondaryColor)',
                              border: '1px solid var(--secondaryColor)',
                            }}
                            onClick={() => onRemoveOverrideItem(i, j)}
                          >
                            Remove SKU
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                  <Button
                    style={{
                      color: 'var(--secondaryColor)',
                      backgroundColor: 'var(--primaryColor)',
                      border: '1px solid var(--primaryColor)',
                    }}
                    onClick={() => onAddOverrideItem(i)}
                  >
                    Add SKU
                  </Button>
                  <Button
                    style={{
                      marginRight: '5px',
                      color: 'var(--primaryColor)',
                      backgroundColor: 'var(--secondaryColor)',
                      border: '1px solid var(--secondaryColor)',
                    }}
                    onClick={() => onRemoveOverride(i)}
                  >
                    Remove Day
                  </Button>
                </div>
              );
            })} */}

            <Table striped bordered hover className="table">
              <thead>
                <tr>
                  <th style={{ color: 'black' }}>Day</th>
                  <th style={{ color: 'black' }}>Sku</th>
                  <th style={{ color: 'black' }}>Override Commission (RM)</th>
                </tr>
              </thead>
              <tbody>
                {rule.override.map((or, i) => (
                  [
                    ...or.items.map((item, index) => (
                      <tr key={index}>
                        {index === 0 && (
                          <td rowSpan={or.items.length}>
                            <Form.Group className="mb-3 smallTitle">
                              <Form.Select
                                onChange={(event) => onChangeOverrideDay(i, event.target.value)}
                              >
                                {new Array(31)
                                  .fill(0)
                                  .map((_, i) => `${i + 1}`.padStart(2, '0'))
                                  .filter(
                                    (value) =>
                                      value === or.day ||
                                      !rule.override.find((or) => or.day === value),
                                  )
                                  .map((value) => (
                                    <option selected={value === or.day} key={value} value={value}>
                                      {value}
                                    </option>
                                  ))}
                              </Form.Select>
                            </Form.Group>
                          </td>
                        )}
                        <td>
                          <Form.Group className="mb-3 smallTitle">
                            <Form.Control
                              onChange={(event) => onChangeOverrideSKU(i, index, event.target.value)}
                              value={item.sku}
                              type="text"
                            />
                          </Form.Group>
                        </td>
                        <td>
                          <Form.Group className="mb-3 smallTitle">
                            <Form.Control
                              onChange={(event) => onChangeOverrideCommission(i, index, event.target.value)}
                              value={item.commission}
                              type="number"
                            />
                          </Form.Group>
                        </td>
                      </tr>
                    )),
                    <tr key={`add-sku-${i}`}>
                      <td colSpan={3}>
                        <Button
                          style={{
                            color: 'var(--secondaryColor)',
                            backgroundColor: 'var(--primaryColor)',
                            border: '1px solid var(--primaryColor)',
                          }}
                          onClick={() => onAddOverrideItem(i)}
                        >
                          Add SKU
                        </Button>
                      </td>
                    </tr>,
                  ]
                ))}
              </tbody>
            </Table>

            <Button
              style={{
                color: 'var(--secondaryColor)',
                backgroundColor: 'var(--primaryColor)',
                border: '1px solid var(--primaryColor)',
              }}
              onClick={onAddNewOverride}
            >
              Add Day
            </Button>
          </div>
        </div>

        <div
          style={{
            marginBottom: '100px',
          }}
        ></div>
        <ButtonGroup
          className="me-2"
          aria-label="Second group"
          style={{
            position: 'fixed',
            right: '3%',
            bottom: '5%',
          }}
        >
          <Button
            style={{
              marginRight: '5px',
              color: 'var(--primaryColor)',
              backgroundColor: 'var(--secondaryColor)',
              border: '1px solid var(--secondaryColor)',
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={busy}
            onClick={onSubmit}
            style={{
              color: 'var(--secondaryColor)',
              backgroundColor: 'var(--primaryColor)',
              border: '1px solid var(--primaryColor)',
            }}
          >
            Save
          </Button>
        </ButtonGroup>
      </Form>
    </Container>
  );
};
